import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import './charger-remote-controls.scss';

const BLUE = { color: '#005293' };
const STATUS_BADGE = { Available: 'badge-available', Charging: 'badge-charging', Reserved: 'badge-reserved', Faulted: 'badge-faulted', Offline: 'badge-secondary' };
const REPLY_BADGE = { Accepted: 'bg-success', Unlocked: 'bg-success', Scheduled: 'bg-warning text-dark', Rejected: 'bg-danger' };

function ago(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function ChargerRemoteControlsPage() {
  const [chargers, setChargers] = useState([]);
  const [siteFilter, setSiteFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [logs, setLogs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [startConn, setStartConn] = useState('1');
  const [startTag, setStartTag] = useState('');
  const [stopTx, setStopTx] = useState('');
  const [trigger, setTrigger] = useState('MeterValues');
  const [pending, setPending] = useState(null); // { label, body }
  const [reason, setReason] = useState('');
  const [reasonErr, setReasonErr] = useState('');
  const [sending, setSending] = useState(false);
  const terminalRef = useRef(null);
  const requested = useRef(new URLSearchParams(window.location.search).get('charger_id'));

  // The stream shows two things, never invented ones: the charger's real OCPP frames (when it holds a live
  // socket to ChargeOS) and the result the server returned for each command sent from this page.
  const [frames, setFrames] = useState([]);
  const [connected, setConnected] = useState(false);
  const [clearedAt, setClearedAt] = useState(0);
  const appendLog = useCallback((direction, action, payload) => {
    setLogs((l) => [...l, { key: `r-${Date.now()}-${l.length}`, direction, action, payload, at: Date.now(), result: true }]);
  }, []);

  const targetId = target?.id;
  useEffect(() => {
    setFrames([]);
    setConnected(false);
    if (!targetId) return undefined;
    let live = true;
    const pull = () => {
      api.getOnboardingCharger(targetId).then((c) => live && setConnected(!!c.connected)).catch(() => {});
      api.listChargerMessages(targetId, 60).then((m) => live && setFrames(Array.isArray(m) ? m : [])).catch(() => {});
    };
    pull();
    const t = setInterval(pull, 4000);
    return () => { live = false; clearInterval(t); };
  }, [targetId]);

  const stream = [
    ...frames.filter((f) => f.kind !== 'connection' || f.action).map((f) => ({
      key: `f-${f.id}`, at: new Date(f.at).getTime(), action: f.action || f.kind, payload: f.payload,
      direction: f.kind === 'error' || f.action === 'Rejected' ? 'error' : f.direction === 'out' ? 'outbound' : 'inbound', kind: f.kind,
    })),
    ...logs,
  ].filter((l) => l.at > clearedAt).sort((a, b) => a.at - b.at);
  const streamLength = stream.length;

  useEffect(() => {
    if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [streamLength]);

  const loadAudit = useCallback(async (id) => {
    try {
      const r = await api.listNocCommands(id, { page: 1, pageSize: 10 });
      setAudit(r.data);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const selectCharger = useCallback(async (c) => {
    setTarget(c);
    setSearch(`${c.ocppId} (${[c.vendor, c.model].filter(Boolean).join(' ')})`);
    try {
      const d = await api.getNocCharger(c.id);
      setDetail(d);
      setStartConn(String(d.connectors?.[0]?.connectorIndex ?? 1));
    } catch (e) {
      setError(e.message);
    }
    loadAudit(c.id);
  }, [loadAudit]);

  useEffect(() => {
    let live = true;
    api.listNocChargers({ page: 1, pageSize: 100 }).then(({ data }) => {
      if (!live) return;
      setChargers(data);
      const found = data.find((c) => c.ocppId === requested.current) || data[0];
      if (found) selectCharger(found);
    }).catch((e) => live && setError(e.message));
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sites = [...new Set(chargers.map((c) => c.site).filter(Boolean))];
  const q = search.toLowerCase().trim();
  const matches = chargers.filter((c) => (siteFilter === 'ALL' || c.site === siteFilter)
    && (!q || (target && search.startsWith(target.ocppId)) || `${c.ocppId} ${c.vendor} ${c.model} ${c.site}`.toLowerCase().includes(q)));

  const ask = (label, body) => {
    setReason('');
    setReasonErr('');
    setPending({ label, body });
  };

  const send = async () => {
    if (reason.trim().length < 3) { setReasonErr('Operational reason notes are required.'); return; }
    setSending(true);
    const { body } = pending;
    const { command, ...rest } = body;
    try {
      const res = await api.sendNocCommand(target.id, { ...body, reason: reason.trim() });
      appendLog(res.replyStatus === 'Rejected' ? 'error' : 'inbound', `${command}Response`, { request: rest, status: res.replyStatus, ...(res.replyDetail ? { reason: res.replyDetail } : {}), ...(res.requestId ? { requestId: res.requestId } : {}) });
      setToast({ ok: res.replyStatus !== 'Rejected', msg: `${command} ${res.replyStatus} by ${target.ocppId}` });
      setPending(null);
      loadAudit(target.id);
      api.getNocCharger(target.id).then(setDetail).catch(() => {});
    } catch (e) {
      appendLog('error', `${command}Error`, { request: rest, message: e.message });
      setToast({ ok: false, msg: e.message });
      setPending(null);
    } finally {
      setSending(false);
      setTimeout(() => setToast(null), 5000);
    }
  };

  const connectors = detail?.connectors || [];
  const connLabel = (k) => `Connector #${k.connectorIndex} (${k.type} - ${k.maxPowerKw}kW)`;
  const connSummary = connectors.length ? `${connectors.length} Connectors (${[...new Set(connectors.map((k) => k.type))].join('/')})` : '—';

  return (
    <div className="container-fluid py-4" id="remote-control-container">
      {toast && (
        <div className={`toast-lite alert ${toast.ok ? 'alert-success' : 'alert-danger'} shadow`} role="status" id="remote-toast">{toast.msg}</div>
      )}
      <div className="row mb-3">
        <div className="col-12">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb bg-transparent p-0 mb-1">
              <li className="breadcrumb-item"><Link to="/noc-fleet-monitoring-alarms" style={BLUE}><i className="fa fa-shield-alt me-1" />NOC Fleet Monitoring</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Remote Controls</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12 d-flex flex-wrap justify-content-between align-items-center">
          <div>
            <h4 className="fw-bold mb-1" style={BLUE}><i className="fas fa-terminal me-2" />Charger Remote Control Console</h4>
            <p className="text-muted small mb-0">Send OCPP 1.6-J / 2.0.1 control commands and see each result. Chargers with a live socket also show their real OCPP messages.</p>
          </div>
          <div className="d-flex flex-wrap align-items-end gap-2 mt-2 mt-lg-0">
            <div className="me-2 mb-2 mb-md-0">
              <label className="text-muted small me-1 mb-0 fw-bold" htmlFor="select-site-filter">Site:</label>
              <select className="form-select form-select-sm" id="select-site-filter" style={{ width: 200 }} value={siteFilter} onChange={(e) => { setSiteFilter(e.target.value); setListOpen(true); }}>
                <option value="ALL">All Plazas ({chargers.length} Chargers)</option>
                {sites.map((s) => <option key={s} value={s}>{s} ({chargers.filter((c) => c.site === s).length} Chargers)</option>)}
              </select>
            </div>
            <div className="position-relative me-2 mb-2 mb-md-0" style={{ width: 280 }}>
              <label className="text-muted small me-1 mb-0 fw-bold" htmlFor="charger-search-input">Target Charger:</label>
              <div className="input-group input-group-sm">
                <input type="text" className="form-control" id="charger-search-input" placeholder="Search OCPP ID, Model..." value={search} autoComplete="off"
                  onFocus={() => setListOpen(true)} onChange={(e) => { setSearch(e.target.value); setListOpen(true); }} />
                <button className="btn btn-outline-secondary" type="button" id="btn-toggle-charger-list" aria-label="Toggle charger list" onClick={() => setListOpen((o) => !o)}>
                  <i className="fas fa-chevron-down" />
                </button>
              </div>
              {listOpen && (
                <div className="dropdown-menu dropdown-menu-end p-0 shadow border show" id="charger-dropdown-results" style={{ maxHeight: 280, overflowY: 'auto', minWidth: 300, right: 0 }}>
                  {matches.length === 0 && <div className="p-3 text-center text-muted small"><i className="fas fa-search me-1" />No matching chargers found in fleet.</div>}
                  {matches.map((c) => (
                    <a key={c.id} className="dropdown-item p-2 border-bottom charger-option-item" href="#select" data-id={c.ocppId}
                      onClick={(e) => { e.preventDefault(); setListOpen(false); selectCharger(c); }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <strong className="small text-dark">{c.ocppId}</strong>
                        <span className={`badge ${STATUS_BADGE[c.status] || 'badge-secondary'}`}>{c.status}</span>
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>{c.vendor} {c.model} &bull; {c.site}</div>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <span className={`badge py-2 px-3 fw-bold ${connected ? 'badge-available' : 'badge-secondary'}`} id="badge-connection-status" title={connected ? 'This charger holds a live OCPP WebSocket to ChargeOS' : 'No charger is connected under this ID: replies to commands are simulated'}>
              <i className="fas fa-circle me-1" style={{ fontSize: 8 }} /> {connected ? 'Live socket' : 'Simulated charger (not connected)'}
            </span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="row">
        <div className="col-12 col-lg-7 mb-4">
          <div className="card border shadow-sm mb-4">
            <div className="card-body p-3">
              <div className="row align-items-center">
                <div className="col-md-3 border-end text-center text-md-start">
                  <div className="text-muted small text-uppercase fw-bold">Station Model</div>
                  <div className="fw-bold text-dark" id="station-info-model">{target ? `${target.vendor || ''} ${target.model || ''}` : '—'}</div>
                  <span className="badge bg-primary small" id="station-info-protocol">OCPP {target?.ocppVersion || '1.6-J'}</span>
                </div>
                <div className="col-md-3 border-end text-center text-md-start mt-2 mt-md-0">
                  <div className="text-muted small text-uppercase fw-bold">Site / Plaza</div>
                  <div className="fw-bold text-dark" id="station-info-site">{target?.site || '—'}</div>
                  <span className="small text-muted" id="station-info-ip">{target?.ocppId}</span>
                </div>
                <div className="col-md-3 border-end text-center text-md-start mt-2 mt-md-0">
                  <div className="text-muted small text-uppercase fw-bold">Operating Status</div>
                  <div><span className={`badge px-2 py-1 ${STATUS_BADGE[detail?.status || target?.status] || 'badge-secondary'}`} id="station-info-status">{detail?.status || target?.status || '—'}</span></div>
                  <span className="small text-muted" id="station-info-connectors">{connSummary}</span>
                </div>
                <div className="col-md-3 text-center text-md-start mt-2 mt-md-0">
                  <div className="text-muted small text-uppercase fw-bold">Last Heartbeat</div>
                  <div className="fw-bold" style={BLUE} id="station-info-hb">{ago(detail?.lastHeartbeat || target?.lastHeartbeat)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-play-circle text-success me-2" />Transaction Controls</h6>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <div className="p-3 rounded bg-light border h-100">
                    <h6 className="fw-bold mb-2" style={BLUE}>RemoteStartTransaction</h6>
                    <div className="mb-2">
                      <label className="small text-muted fw-bold mb-1" htmlFor="cmd-start-connector">Connector ID</label>
                      <select className="form-select form-select-sm" id="cmd-start-connector" value={startConn} onChange={(e) => setStartConn(e.target.value)}>
                        {connectors.map((k) => <option key={k.connectorIndex} value={k.connectorIndex}>{connLabel(k)}</option>)}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="small text-muted fw-bold mb-1" htmlFor="cmd-start-tag">Authorization Tag / idTag</label>
                      <input type="text" className="form-control form-control-sm font-monospace" id="cmd-start-tag" maxLength={20} placeholder="e.g. NOC-TEST-01 (max 20 characters)" value={startTag} onChange={(e) => setStartTag(e.target.value)} />
                      <div className="form-text">A remote start from here is for testing: it is logged but not billed to a driver.</div>
                    </div>
                    <button className="btn btn-sm btn-success w-100 fw-bold" id="btn-cmd-start" disabled={!target || !startTag.trim()}
                      onClick={() => ask(`RemoteStartTransaction on connector #${startConn}`, { command: 'RemoteStartTransaction', connectorIndex: Number(startConn), idTag: startTag.trim() })}>
                      <i className="fas fa-play me-1" /> Send RemoteStart
                    </button>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 rounded bg-light border h-100">
                    <h6 className="fw-bold text-danger mb-2">RemoteStopTransaction</h6>
                    <div className="mb-2">
                      <label className="small text-muted fw-bold mb-1" htmlFor="cmd-stop-tx">Active Transaction ID</label>
                      <input type="text" inputMode="numeric" className="form-control form-control-sm font-monospace" id="cmd-stop-tx" placeholder="e.g. 847201" value={stopTx} onChange={(e) => setStopTx(e.target.value.replace(/\D/g, ''))} />
                    </div>
                    <p className="small text-muted mb-3">The transaction ID is shown in the charger&apos;s StartTransaction message and in the command log below. The reason you enter when confirming is recorded in the audit log.</p>
                    <button className="btn btn-sm btn-danger w-100 fw-bold" id="btn-cmd-stop" disabled={!target || !stopTx}
                      onClick={() => ask(`RemoteStopTransaction #${stopTx}`, { command: 'RemoteStopTransaction', transactionId: Number(stopTx) })}>
                      <i className="fas fa-stop me-1" /> Send RemoteStop
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border shadow-sm">
            <div className="card-header bg-white py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-tools me-2" style={{ color: '#d97706' }} />Hardware Diagnostic &amp; Maintenance Commands</h6>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-4 mb-3 mb-md-0">
                  <div className="p-3 rounded bg-light border text-center h-100">
                    <i className="fas fa-unlock-alt fa-2x mb-2" style={{ color: '#d97706' }} />
                    <h6 className="fw-bold text-dark mb-1">Unlock Connector</h6>
                    <p className="small text-muted mb-3">Releases mechanical latch cable lock</p>
                    <div className="btn-group btn-group-sm w-100 flex-wrap">
                      {connectors.length === 0 && <button className="btn btn-outline-warning fw-bold" disabled>No connectors</button>}
                      {connectors.map((k) => (
                        <button key={k.connectorIndex} className="btn btn-outline-warning fw-bold btn-cmd-unlock" data-gun={k.connectorIndex} disabled={!target} title={connLabel(k)}
                          onClick={() => ask(`UnlockConnector on connector #${k.connectorIndex}`, { command: 'UnlockConnector', connectorIndex: k.connectorIndex })}>#{k.connectorIndex} {k.type}</button>
                      ))}
                    </div>
                    <div className="btn-group btn-group-sm w-100 mt-2">
                      <button className="btn btn-outline-secondary fw-bold btn-cmd-availability" disabled={!target}
                        onClick={() => ask('ChangeAvailability: Operative (whole charger)', { command: 'ChangeAvailability', connectorIndex: 0, availability: 'Operative' })}>Operative</button>
                      <button className="btn btn-outline-danger fw-bold btn-cmd-availability" disabled={!target}
                        onClick={() => ask('ChangeAvailability: Inoperative (whole charger)', { command: 'ChangeAvailability', connectorIndex: 0, availability: 'Inoperative' })}>Inoperative</button>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3 mb-md-0">
                  <div className="p-3 rounded bg-light border text-center h-100">
                    <i className="fas fa-power-off fa-2x text-danger mb-2" />
                    <h6 className="fw-bold text-dark mb-1">Reboot Station</h6>
                    <p className="small text-muted mb-3">Triggers controller reset cycle</p>
                    <div className="btn-group btn-group-sm w-100">
                      <button className="btn btn-outline-secondary fw-bold btn-cmd-reset" data-type="Soft" disabled={!target}
                        onClick={() => ask('Soft Reset', { command: 'Reset', resetType: 'Soft' })}>Soft Reset</button>
                      <button className="btn btn-outline-danger fw-bold btn-cmd-reset" data-type="Hard" disabled={!target}
                        onClick={() => ask('Hard Reset', { command: 'Reset', resetType: 'Hard' })}>Hard Power</button>
                    </div>
                    <button className="btn btn-sm btn-outline-secondary fw-bold w-100 mt-2" id="btn-cmd-clear-cache" disabled={!target}
                      onClick={() => ask('ClearCache', { command: 'ClearCache' })}>Clear Cache</button>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 rounded bg-light border text-center h-100">
                    <i className="fas fa-broadcast-tower fa-2x text-info mb-2" />
                    <h6 className="fw-bold text-dark mb-1">Trigger Message</h6>
                    <p className="small text-muted mb-2">Forces immediate telemetry push</p>
                    <label className="visually-hidden" htmlFor="cmd-trigger-type">Message type</label>
                    <select className="form-select form-select-sm mb-2" id="cmd-trigger-type" value={trigger} onChange={(e) => setTrigger(e.target.value)}>
                      <option value="MeterValues">MeterValues</option>
                      <option value="StatusNotification">StatusNotification</option>
                      <option value="Heartbeat">Heartbeat</option>
                    </select>
                    <button className="btn btn-sm btn-outline-info fw-bold w-100" id="btn-cmd-trigger" disabled={!target}
                      onClick={() => ask(`TriggerMessage ${trigger}`, { command: 'TriggerMessage', requestedMessage: trigger })}>Trigger</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-5 mb-4">
          <div className="card border shadow-sm h-100">
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
              <span className="fw-bold" style={BLUE}><i className="fas fa-stream me-2" />OCPP messages &amp; command results</span>
              <div>
                <button className="btn btn-outline-secondary me-1" id="btn-clear-log" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => { setLogs([]); setClearedAt(Date.now()); }}><i className="fas fa-trash-alt me-1" />Clear</button>
                <button className="btn btn-outline-primary" id="btn-copy-log" style={{ fontSize: 11, padding: '2px 8px' }}
                  onClick={() => { navigator.clipboard?.writeText(stream.map((l) => `${new Date(l.at).toISOString()} ${l.direction} ${l.action} ${JSON.stringify(l.payload)}`).join('\n')); setToast({ ok: true, msg: 'Console log copied to clipboard.' }); setTimeout(() => setToast(null), 3000); }}>
                  <i className="fas fa-copy me-1" />Copy
                </button>
              </div>
            </div>
            <div className="card-body p-2 rounded-bottom" style={{ height: 600, overflowY: 'auto', backgroundColor: '#0f172a' }} id="ocpp-terminal-window" ref={terminalRef}>
              <div className="font-monospace small p-2" id="ocpp-log-stream">
                <div style={{ color: '#94a3b8', marginBottom: 8 }} id="ocpp-stream-source">
                  {connected
                    ? 'Live socket: real OCPP frames from this charger, plus the result of each command you send.'
                    : 'No charger is connected under this ID, so there are no OCPP frames. Command results below come from the built-in charger simulator.'}
                </div>
                {stream.length === 0 && <div style={{ color: '#64748b' }}>Nothing yet. Send a command to see its result here.</div>}
                {stream.map((l) => {
                  const colour = l.direction === 'error' ? '#f87171' : l.direction === 'outbound' ? '#38bdf8' : '#34d399';
                  let tag = l.direction === 'outbound' ? '\u2192 ChargeOS' : '\u2190 charger';
                  if (l.result) tag = connected ? 'RESULT' : 'RESULT (simulated)';
                  return (
                    <div key={l.key} className={`log-line ${l.direction}`}>
                      <span style={{ color: '#94a3b8' }}>[{new Date(l.at).toLocaleTimeString()}]</span>{' '}
                      <span style={{ color: colour, fontWeight: 'bold' }}>{tag}</span>{' '}
                      <strong style={{ color: '#ffffff' }}>{l.action}</strong>
                      {l.payload != null && <pre style={{ color: l.direction === 'outbound' ? '#7dd3fc' : '#a7f3d0', margin: '4px 0 0 0', fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(l.payload, null, 2)}</pre>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card border shadow-sm">
            <div className="card-header bg-white py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-clipboard-list me-2" />Command Audit Log{target ? ` — ${target.ocppId}` : ''}</h6>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0" id="table-command-audit">
                <thead className="table-light">
                  <tr><th>Timestamp</th><th>Operator</th><th>Command</th><th>Target EVSE</th><th>Reason</th><th>Result</th></tr>
                </thead>
                <tbody>
                  {audit.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">No remote commands have been dispatched to this charger yet.</td></tr>}
                  {audit.map((a) => (
                    <tr key={a.id} className="audit-row">
                      <td className="small">{new Date(a.dispatchedAt).toLocaleString('en-PH')}</td>
                      <td className="small">{a.operator || '—'}</td>
                      <td className="small fw-bold">{a.command}{a.params?.type ? ` (${a.params.type})` : ''}</td>
                      <td className="small">{target?.ocppId}{a.connectorIndex != null ? ` / #${a.connectorIndex}` : ''}</td>
                      <td className="small">{a.reason}</td>
                      <td><span className={`badge ${REPLY_BADGE[a.replyStatus] || 'bg-secondary'}`}>{a.replyStatus || 'Pending'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {pending && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="remote-confirm-title" id="modal-confirm-command">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="remote-confirm-title">Confirm Remote Command</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setPending(null)} />
              </div>
              <div className="modal-body">
                <p>Send <strong>{pending.label}</strong> to <strong>{target?.ocppId}</strong>?</p>
                <label className="form-label fw-bold small" htmlFor="remote-reason">Operational reason notes <span className="text-danger">*</span></label>
                <textarea id="remote-reason" className={`form-control ${reasonErr ? 'is-invalid' : ''}`} rows={3} maxLength={500} value={reason}
                  onChange={(e) => { setReason(e.target.value); setReasonErr(''); }} placeholder="e.g. Driver reported stuck cable; ticket #4821" />
                {reasonErr && <div className="invalid-feedback" id="remote-reason-error">{reasonErr}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPending(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" id="btn-send-command" onClick={send} disabled={sending}>
                  {sending ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-paper-plane me-1" />}Send Command
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
